import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiFetch } from '../utils/httpClient';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserShield, 
  faUser, 
  faTrash, 
  faCheck, 
  faTimes,
  faSpinner,
  faPlus,
  faCircleCheck,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';

const Section = styled.section`
  margin-bottom: var(--spacing-10);
  width: 100%;
  min-width: 0;
  overflow-x: visible;
  max-width: none;
  margin-left: 0;
  position: relative;
`;

const SectionTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: var(--spacing-2);
  color: var(--color-text-primary);
  text-align: left;
`;

const SectionDescription = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-8);
  font-size: 0.95rem;
  text-align: left;
`;

const ControlsBar = styled.div`
  display: flex;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const AddUserButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-5);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-accent);
  background: var(--color-accent);
  color: #fff;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: var(--color-accent-light);
    border-color: var(--color-accent-light);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ControlsLeft = styled.div`
  display: flex;
  gap: var(--spacing-4);
  flex-wrap: wrap;
  align-items: center;
  flex: 1;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 150px;
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 0.95rem;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    min-width: 120px;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: 0.875rem;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const FilterSelect = styled.select`
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 0.95rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const TableWrapper = styled.div`
  width: max-content;
  margin-right: auto;
`;

const TableContainer = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow-y: auto;
  overflow-x: auto;
  max-height: 70vh;
  width: 100%;
  position: relative;
  
  /* Smooth scrolling */
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    overflow-x: auto;
    overflow-y: auto;
    max-height: 60vh;
  }
  
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 4px;
    
    &:hover {
      background: var(--color-text-tertiary);
    }
  }
`;

const Table = styled.table`
  width: auto;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  margin: 0;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    min-width: max-content;
    table-layout: auto;
  }
`;

const TableHeader = styled.thead`
  background: var(--color-surface-alt);
  border-bottom: 2px solid var(--color-border);
`;

const TableHeaderCell = styled.th<{ $width?: string }>`
  padding: var(--spacing-4) var(--spacing-5);
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-secondary);
  white-space: nowrap;
  background: var(--color-surface-alt);
  position: sticky;
  top: 0;
  z-index: 100;
  ${props => props.$width ? `
    width: ${props.$width};
    min-width: ${props.$width};
    max-width: ${props.$width};
  ` : ''}
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $isEven?: boolean; $clickable?: boolean }>`
  border-bottom: 1px solid var(--color-border);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: inset 4px 0 0 var(--color-accent);
    cursor: pointer;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td<{ $width?: string }>`
  padding: var(--spacing-4) var(--spacing-5);
  font-size: 0.95rem;
  color: var(--color-text-primary);
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  ${props => props.$width ? `
    width: ${props.$width};
    min-width: ${props.$width};
    max-width: ${props.$width};
  ` : ''}
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'error' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$variant) {
      case 'success': return 'rgba(74, 222, 128, 0.2)';
      case 'warning': return 'rgba(250, 204, 21, 0.2)';
      case 'error': return 'rgba(239, 68, 68, 0.2)';
      case 'info': return 'rgba(59, 130, 246, 0.2)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return '#4ade80';
      case 'warning': return '#facc15';
      case 'error': return '#ef4444';
      case 'info': return '#3b82f6';
      default: return 'var(--color-text-secondary)';
    }
  }};
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-10);
  color: var(--color-text-secondary);
  gap: var(--spacing-4);
`;

const ErrorState = styled.div`
  padding: var(--spacing-6);
  background: rgba(var(--color-error-rgb), 0.1);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  text-align: center;
`;

const EmptyState = styled.div`
  padding: var(--spacing-10);
  text-align: center;
  color: var(--color-text-secondary);
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spacing-6);
  padding: var(--spacing-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  width: 100%;
`;

const PaginationInfo = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.875rem;
`;

const PaginationButton = styled.button`
  padding: var(--spacing-2) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    background: var(--color-surface-alt);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface User {
  id: string;
  email: string;
  role: 'user' | 'admin' | 'system_admin' | 'system_user';
  organization_id?: string | null;
  organization_role?: string | null;
  email_verified: boolean;
  subscription_status: string;
  subscription_type: string;
  created_at: string;
  last_login_at?: string | null;
}

interface UsersResponse {
  users: User[];
  total: number;
  skip: number;
  limit: number;
}

const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: var(--spacing-4);
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-6);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-4);
  color: var(--color-text-primary);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  margin-bottom: var(--spacing-4);
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--color-text-primary);
`;

const Input = styled.input`
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-text-primary);
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const Select = styled.select`
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-text-primary);
  font-size: 0.95rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  margin-top: var(--spacing-6);
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: var(--spacing-3) var(--spacing-5);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent);
    color: #fff;
    border-color: var(--color-accent);
    &:hover:not(:disabled) {
      background: var(--color-accent-light);
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text-primary);
    &:hover {
      background: var(--color-surface-alt);
    }
  `}
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  padding: var(--spacing-4) var(--spacing-5);
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.08));
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: var(--radius-lg);
  color: #6ee7b7;
  margin-bottom: var(--spacing-4);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
  backdrop-filter: blur(8px);
  transition: all 0.2s var(--transition-smooth);

  &:hover {
    border-color: rgba(16, 185, 129, 0.35);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: #10b981;
    font-size: 1.125rem;
  }

  strong {
    color: #34d399; /* Slightly brighter for emphasis */
    font-weight: 600;
  }
`;

export const UserManagementSection: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'admin'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'system_admin' | 'system_user'>('system_user');
  const [newUserSubscriptionType, setNewUserSubscriptionType] = useState('free');
  const [newUserOrganizationId, setNewUserOrganizationId] = useState<string>('');
  const [newUserOrganizationRole, setNewUserOrganizationRole] = useState<string>('member');
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState<any | null>(null);
  const [addingUser, setAddingUser] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [editOrganizationId, setEditOrganizationId] = useState<string>('');
  const [editOrganizationRole, setEditOrganizationRole] = useState<string>('');
  const [editSubscriptionStatus, setEditSubscriptionStatus] = useState<string>('');
  const [editSubscriptionType, setEditSubscriptionType] = useState<string>('free');
  const limit = 20;

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const roleParam = roleFilter !== 'all' ? `&role=${roleFilter}` : '';
      const response = await apiFetch(
        `/api/v1/auth/admin/users?skip=${skip}&limit=${limit}${roleParam}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data: UsersResponse = await response.json();
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [skip, roleFilter]);

  const handleRowClick = (user: any) => {
    setSelectedUser(user);
    setEditOrganizationId(user.organization_id || '');
    setEditOrganizationRole(user.organization_role || 'member');
    setEditSubscriptionStatus(user.subscription_status || 'active');
    setEditSubscriptionType(user.subscription_type || 'free');
    setShowActionsModal(true);
  };

  const handleSaveUserChanges = async () => {
    if (!selectedUser) return;

    setActionLoading(selectedUser.email);
    setError(null);

    try {
      const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
      const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';

      // Only system admins can update organization and subscription
      if (isSystemAdmin) {
        // Update organization/role
        const response = await apiFetch(`/api/v1/auth/admin/users/${encodeURIComponent(selectedUser.email)}/organization`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organization_id: editOrganizationId || null,
            organization_role: editOrganizationId ? editOrganizationRole : null,
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.detail || 'Failed to update user');
        }

        // Update subscription status if changed (only for active/inactive)
        if (editSubscriptionStatus !== selectedUser.subscription_status && 
            (editSubscriptionStatus === 'active' || editSubscriptionStatus === 'inactive')) {
          await handleUpdateSubscriptionStatus(
            selectedUser.email, 
            editSubscriptionStatus as 'active' | 'inactive'
          );
        }

        // Update subscription type if status is active and type changed
        if (editSubscriptionStatus === 'active' && editSubscriptionType !== selectedUser.subscription_type) {
          await handleUpdateSubscriptionType(
            selectedUser.email,
            editSubscriptionType
          );
        }
      } else if (isOrgAdmin) {
        // Organization admins can only update organization role for non-admin users
        // Check if organization role actually changed
        if (editOrganizationRole !== selectedUser.organization_role) {
          const response = await apiFetch(`/api/v1/auth/admin/users/${encodeURIComponent(selectedUser.email)}/organization`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              organization_role: editOrganizationRole || null,
            }),
          });

          if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.detail || 'Failed to update user');
          }
        }
      }

      await fetchUsers();
      setShowActionsModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  // Fetch organizations list for dropdown (system admins) or current organization (org admins)
  useEffect(() => {
    const fetchOrganizations = async () => {
      const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
      const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';

      if (isSystemAdmin) {
        // System admins can see all organizations
        setLoadingOrganizations(true);
        try {
          const response = await apiFetch('/api/v1/organizations/admin/organizations?skip=0&limit=1000');
          if (response.ok) {
            const data = await response.json();
            setOrganizations(data.organizations || []);
          }
        } catch (err) {
          console.error('Failed to fetch organizations:', err);
        } finally {
          setLoadingOrganizations(false);
        }
      } else if (isOrgAdmin && currentUser?.organization_id) {
        // Organization admins fetch their own organization
        setLoadingOrganizations(true);
        try {
          const response = await apiFetch(`/api/v1/organizations/admin/organizations/${currentUser.organization_id}`);
          if (response.ok) {
            const org = await response.json();
            setCurrentOrganization(org);
            // Auto-set organization ID and subscription type
            setNewUserOrganizationId(currentUser.organization_id);
            // Map subscription_tier to subscription_type
            const subscriptionTier = org.subscription_tier || 'free';
            setNewUserSubscriptionType(subscriptionTier === 'free' ? 'free' : subscriptionTier === 'paid' ? 'paid' : 'admin');
          }
        } catch (err) {
          console.error('Failed to fetch organization:', err);
        } finally {
          setLoadingOrganizations(false);
        }
      }
    };

    fetchOrganizations();
  }, [currentUser]);

  // Auto-set organization and subscription when modal opens for organization admins
  useEffect(() => {
    if (showAddUserModal) {
      const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
      const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';
      
      if (isOrgAdmin && !isSystemAdmin && currentUser?.organization_id) {
        setNewUserOrganizationId(currentUser.organization_id);
        // Set subscription type from organization
        if (currentOrganization) {
          const subscriptionTier = currentOrganization.subscription_tier || 'free';
          setNewUserSubscriptionType(subscriptionTier === 'free' ? 'free' : subscriptionTier === 'paid' ? 'paid' : 'admin');
        }
      }
    }
  }, [showAddUserModal, currentUser, currentOrganization]);


  const handleDeleteUserClick = (email: string) => {
    if (email === currentUser?.email) {
      setError('You cannot delete your own account');
      return;
    }
    setUserToDelete(email);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    setActionLoading(userToDelete);
    setShowDeleteConfirmModal(false);
    setShowActionsModal(false);
    setSelectedUser(null);
    
    try {
      const response = await apiFetch(
        `/api/v1/auth/admin/users/${encodeURIComponent(userToDelete)}`,
        { method: 'DELETE' }
      );
      
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'Failed to delete user');
      }
      
      await fetchUsers();
      setUserToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateSubscriptionStatus = async (email: string, status: 'active' | 'inactive') => {
    setActionLoading(email);
    try {
      const response = await apiFetch(
        `/api/v1/auth/admin/subscriptions/${encodeURIComponent(email)}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        }
      );
      
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'Failed to update subscription status');
      }
      
      await fetchUsers();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to update subscription status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateSubscriptionType = async (email: string, subscriptionType: string) => {
    try {
      const response = await apiFetch(
        `/api/v1/auth/admin/subscriptions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email,
            subscription_type: subscriptionType
          }),
        }
      );
      
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'Failed to update subscription type');
      }
    } catch (err) {
      throw err; // Re-throw to be handled by parent
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail.trim()) {
      window.alert('Please enter an email address');
      return;
    }

    const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
    const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';
    
    // For organization admins, auto-set organization and subscription
    let finalOrganizationId = newUserOrganizationId;
    let finalSubscriptionType = newUserSubscriptionType;
    
    if (isOrgAdmin && !isSystemAdmin) {
      // Organization admins must add users to their organization
      if (!currentUser?.organization_id) {
        window.alert('You must belong to an organization to add users');
        return;
      }
      finalOrganizationId = currentUser.organization_id;
      // Use organization's subscription_tier
      if (currentOrganization) {
        const subscriptionTier = currentOrganization.subscription_tier || 'free';
        finalSubscriptionType = subscriptionTier === 'free' ? 'free' : subscriptionTier === 'paid' ? 'paid' : 'admin';
      }
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserEmail)) {
      window.alert('Please enter a valid email address');
      return;
    }

    setAddingUser(true);
    setError(null);

    try {
      const requestBody: any = {
        email: newUserEmail.trim(),
        role: newUserRole,
        subscription_type: finalSubscriptionType,
      };

      // Add organization fields if organization is selected
      if (finalOrganizationId) {
        requestBody.organization_id = finalOrganizationId;
        requestBody.organization_role = newUserOrganizationRole;
      }

      const response = await apiFetch('/api/v1/auth/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'Failed to create user');
      }

      await response.json();
      setUserCreated(true);
      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
      window.alert(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setNewUserEmail('');
    setNewUserRole('system_user');
    setNewUserSubscriptionType('free');
    
    // Reset organization - but keep it set for org admins
    const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
    const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';
    
    if (isOrgAdmin && !isSystemAdmin && currentUser?.organization_id) {
      // Keep organization set for org admins
      setNewUserOrganizationId(currentUser.organization_id);
      // Reset subscription type to match organization
      if (currentOrganization) {
        const subscriptionTier = currentOrganization.subscription_tier || 'free';
        setNewUserSubscriptionType(subscriptionTier === 'free' ? 'free' : subscriptionTier === 'paid' ? 'paid' : 'admin');
      }
    } else {
      setNewUserOrganizationId('');
      }
    
    setNewUserOrganizationRole('member');
    setUserCreated(false);
    setError(null);
  };


  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.subscription_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge $variant="success">Active</Badge>;
      case 'inactive':
        return <Badge $variant="error">Inactive</Badge>;
      case 'pending':
        return <Badge $variant="warning">Pending</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Section>
      <SectionTitle>User Management</SectionTitle>
      <SectionDescription>Manage users, roles, and subscriptions</SectionDescription>

      <TableWrapper>
        <ControlsBar>
          <ControlsLeft>
            <SearchInput
              type="text"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FilterSelect value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as any)}>
              <option value="all">All Roles</option>
              <option value="user">Users</option>
              <option value="admin">Admins</option>
            </FilterSelect>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </FilterSelect>
          </ControlsLeft>
          <AddUserButton onClick={() => setShowAddUserModal(true)} type="button">
            <FontAwesomeIcon icon={faPlus} />
            Add User
          </AddUserButton>
        </ControlsBar>

        {error ? (
          <ErrorState>{error}</ErrorState>
        ) : filteredUsers.length === 0 && !loading ? (
          <EmptyState>No users found</EmptyState>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell $width="250px">Email</TableHeaderCell>
                    <TableHeaderCell $width="150px">Role</TableHeaderCell>
                    <TableHeaderCell $width="200px">Organization</TableHeaderCell>
                    <TableHeaderCell $width="120px">Status</TableHeaderCell>
                    <TableHeaderCell $width="130px">Verified</TableHeaderCell>
                    <TableHeaderCell $width="160px">Created</TableHeaderCell>
                    <TableHeaderCell $width="160px">Last Login</TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => {
                    const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
                    const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';
                    
                    // System admins can click on any user
                    // Organization admins can click on non-admin users in their organization
                    const isUserAdmin = user.organization_role === 'admin' || user.organization_role === 'owner';
                    const isUserInSameOrg = user.organization_id === currentUser?.organization_id;
                    const canEditUser = isSystemAdmin || (isOrgAdmin && !isUserAdmin && isUserInSameOrg);
                    
                    return (
                    <TableRow 
                      key={user.id}
                      onClick={canEditUser ? () => handleRowClick(user) : undefined}
                      style={{ cursor: canEditUser ? 'pointer' : 'default' }}
                    >
                    <TableCell $width="250px">{user.email}</TableCell>
                    <TableCell $width="150px">
                      {user.role === 'admin' || user.role === 'system_admin' ? (
                        <Badge $variant="info">
                          <FontAwesomeIcon icon={faUserShield} /> System Admin
                        </Badge>
                      ) : (
                        <Badge>
                          <FontAwesomeIcon icon={faUser} /> {user.organization_role ? user.organization_role.charAt(0).toUpperCase() + user.organization_role.slice(1) : 'User'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell $width="200px">
                      {user.organization_id ? (
                        <Badge>
                          <FontAwesomeIcon icon={faBuilding} /> {organizations.find(o => o.id === user.organization_id)?.name || 'Organization'}
                        </Badge>
                      ) : (
                        <span style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>None</span>
                      )}
                    </TableCell>
                    <TableCell $width="120px">{getStatusBadge(user.subscription_status)}</TableCell>
                    <TableCell $width="130px">
                      {user.email_verified ? (
                        <Badge $variant="success">
                          <FontAwesomeIcon icon={faCheck} /> Verified
                        </Badge>
                      ) : (
                        <Badge $variant="warning">
                          <FontAwesomeIcon icon={faTimes} /> Not Verified
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell $width="160px">{formatDate(user.created_at)}</TableCell>
                    <TableCell $width="160px">{formatDate(user.last_login_at)}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination>
              <PaginationInfo>
                Showing {skip + 1}-{Math.min(skip + limit, total)} of {total} users
              </PaginationInfo>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                <PaginationButton
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0 || loading}
                >
                  Previous
                </PaginationButton>
                <PaginationButton
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= total || loading}
                >
                  Next
                </PaginationButton>
              </div>
            </Pagination>
          </>
        )}
      </TableWrapper>

      <Modal $isOpen={showAddUserModal} onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleCloseAddUserModal();
        }
      }}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Add New User</ModalTitle>
          
          {userCreated ? (
            <>
              <SuccessMessage>
                <FontAwesomeIcon icon={faCircleCheck} />
                <div>
                  User created successfully!
                <br />
                <br />
                  A verification email has been sent to <strong>{newUserEmail}</strong>.
                <br />
                  The user will set their own password after verifying their email.
                </div>
              </SuccessMessage>
              
              <ModalButtonGroup>
                <ModalButton onClick={handleCloseAddUserModal} $variant="primary">
                  Done
                </ModalButton>
              </ModalButtonGroup>
            </>
          ) : (
            <>
              {(() => {
                const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
                const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';
                
                return (
            <>
              <FormGroup>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  required
                  disabled={addingUser}
                />
              </FormGroup>

                    {/* Organization field - only for system admins */}
                    {isSystemAdmin && (
              <FormGroup>
                        <Label>Organization (Optional)</Label>
                <Select
                          value={newUserOrganizationId}
                          onChange={(e) => {
                            const orgId = e.target.value;
                            setNewUserOrganizationId(orgId);
                            // If organization selected, set system role to system_user automatically
                            if (orgId) {
                              setNewUserRole('system_user');
                            }
                            // If organization cleared, reset organization role
                            if (!orgId) {
                              setNewUserOrganizationRole('member');
                            }
                          }}
                          disabled={addingUser || loadingOrganizations}
                        >
                          <option value="">No Organization</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </Select>
                      </FormGroup>
                    )}

                    {/* Organization Role field - shown if organization is set (system admin) or always for org admin */}
                    {(newUserOrganizationId || isOrgAdmin) && (
                      <FormGroup>
                        <Label>Organization Role *</Label>
                        <Select
                          value={newUserOrganizationRole}
                          onChange={(e) => setNewUserOrganizationRole(e.target.value)}
                  disabled={addingUser}
                >
                          <option value="member">Member</option>
                          <option value="analyst">Analyst</option>
                          <option value="scout">Scout</option>
                          <option value="coach">Coach</option>
                  <option value="admin">Admin</option>
                          <option value="owner">Owner</option>
                </Select>
              </FormGroup>
                    )}

                    {/* Subscription Type - only for system admins (org admins use org subscription) */}
                    {isSystemAdmin && (
              <FormGroup>
                <Label>Subscription Type</Label>
                <Select
                  value={newUserSubscriptionType}
                  onChange={(e) => setNewUserSubscriptionType(e.target.value)}
                  disabled={addingUser}
                >
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                  <option value="admin">Admin</option>
                </Select>
              </FormGroup>
                    )}

                    {isOrgAdmin && currentOrganization && (
                      <FormGroup>
                        <Label>Subscription Type</Label>
                        <div style={{ 
                          padding: 'var(--spacing-2) var(--spacing-3)', 
                          background: 'var(--color-surface-alt)', 
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--color-text-secondary)',
                          fontSize: '0.875rem'
                        }}>
                          {currentOrganization.subscription_tier || 'free'} (from organization)
                        </div>
                      </FormGroup>
                    )}
                  </>
                );
              })()}

              {!newUserOrganizationId && (
                <FormGroup>
                  <Label>System Role *</Label>
                  <Select
                    value={newUserRole}
                    onChange={(e) => {
                      const newRole = e.target.value as 'system_admin' | 'system_user';
                      setNewUserRole(newRole);
                    }}
                    disabled={addingUser}
                  >
                    <option value="system_user">System User</option>
                    <option value="system_admin">System Admin</option>
                  </Select>
                </FormGroup>
              )}

              {error && (
                <ErrorState style={{ marginBottom: 'var(--spacing-4)' }}>{error}</ErrorState>
              )}

              <ModalButtonGroup>
                <ModalButton onClick={handleCloseAddUserModal} disabled={addingUser}>
                  Cancel
                </ModalButton>
                <ModalButton onClick={handleAddUser} $variant="primary" disabled={addingUser}>
                  {addingUser ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 'var(--spacing-2)' }} />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </ModalButton>
              </ModalButtonGroup>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* User Actions Modal */}
      <Modal $isOpen={showActionsModal} onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowActionsModal(false);
          setSelectedUser(null);
        }
      }}>
        <ModalContent onClick={(e) => e.stopPropagation()} style={{ minWidth: '500px', maxWidth: '600px' }}>
          <ModalTitle style={{ 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            marginBottom: 'var(--spacing-6)'
          }}>
            User Settings - {selectedUser?.email}
          </ModalTitle>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)' }}>
            {(() => {
              const isSystemAdmin = currentUser?.role === 'system_admin' || currentUser?.role === 'admin';
              const isOrgAdmin = currentUser?.organization_role === 'admin' || currentUser?.organization_role === 'owner';
              const isSelectedUserAdmin = selectedUser?.organization_role === 'admin' || selectedUser?.organization_role === 'owner';
              const isCurrentUser = selectedUser?.email === currentUser?.email;
              
              return (
                <>
                  {/* Organization field - only for system admins */}
                  {isSystemAdmin && (
                    <FormGroup>
                      <Label>Organization</Label>
                      <Select
                        value={editOrganizationId}
                        onChange={(e) => {
                          setEditOrganizationId(e.target.value);
                          if (!e.target.value) {
                            setEditOrganizationRole('member');
                          }
                        }}
                        disabled={actionLoading !== null}
                      >
                        <option value="">No Organization</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  )}

                  {/* Organization Role field */}
                  {(editOrganizationId || selectedUser?.organization_id) && (
                    <FormGroup>
                      <Label>Organization Role</Label>
                      <Select
                        value={editOrganizationRole}
                        onChange={(e) => setEditOrganizationRole(e.target.value)}
                        disabled={
                          actionLoading !== null || 
                          (isOrgAdmin && isSelectedUserAdmin) || // Org admin can't change admin users' roles
                          (isOrgAdmin && isCurrentUser) // Org admin can't change their own role
                        }
                      >
                        <option value="member">Member</option>
                        <option value="analyst">Analyst</option>
                        <option value="scout">Scout</option>
                        <option value="coach">Coach</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </Select>
                      {isOrgAdmin && (isSelectedUserAdmin || isCurrentUser) && (
                        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginTop: 'var(--spacing-1)' }}>
                          {isCurrentUser ? 'You cannot change your own role' : 'You cannot change admin users\' roles'}
                        </div>
                      )}
                    </FormGroup>
                  )}

                  {/* Subscription Status - only for system admins */}
                  {isSystemAdmin && (
                    <>
                      <FormGroup>
                        <Label>Subscription Status</Label>
                        <Select
                          value={editSubscriptionStatus}
                          onChange={(e) => setEditSubscriptionStatus(e.target.value)}
                          disabled={actionLoading !== null}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="pending">Pending</option>
                        </Select>
                      </FormGroup>

                      {/* Subscription Type - only shown when status is active */}
                      {editSubscriptionStatus === 'active' && (
                        <FormGroup>
                          <Label>Subscription Type</Label>
                          <Select
                            value={editSubscriptionType}
                            onChange={(e) => setEditSubscriptionType(e.target.value)}
                            disabled={actionLoading !== null}
                          >
                            <option value="free">Free</option>
                            <option value="paid">Paid</option>
                            <option value="admin">Admin</option>
                          </Select>
                        </FormGroup>
                      )}
                    </>
                  )}
                </>
              );
            })()}

            {error && (
              <ErrorState>{error}</ErrorState>
            )}

            <div style={{ 
              display: 'flex', 
              gap: 'var(--spacing-3)', 
              marginTop: 'var(--spacing-2)',
              paddingTop: 'var(--spacing-4)',
              borderTop: '1px solid var(--color-border)'
            }}>
              <ModalButton
                onClick={() => {
                  if (selectedUser && selectedUser.email !== currentUser?.email) {
                    handleDeleteUserClick(selectedUser.email);
                  }
                }}
                style={{ 
                  color: '#ef4444',
                  borderColor: '#ef4444',
                  background: 'transparent',
                  marginRight: 'auto'
                }}
                disabled={actionLoading !== null || selectedUser?.email === currentUser?.email}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete User
              </ModalButton>

              <ModalButton 
                onClick={() => {
                  setShowActionsModal(false);
                  setSelectedUser(null);
                }}
                disabled={actionLoading !== null}
              >
                Cancel
              </ModalButton>
              <ModalButton 
                onClick={handleSaveUserChanges} 
                $variant="primary" 
                disabled={actionLoading !== null}
              >
                {actionLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 'var(--spacing-2)' }} />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </ModalButton>
            </div>
          </div>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal $isOpen={showDeleteConfirmModal} onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowDeleteConfirmModal(false);
          setUserToDelete(null);
        }
      }}>
        <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
          <ModalTitle style={{ color: '#ef4444', marginBottom: 'var(--spacing-4)' }}>
            Delete User
          </ModalTitle>
          
          <div style={{ marginBottom: 'var(--spacing-6)', color: 'var(--color-text-primary)' }}>
            <p style={{ marginBottom: 'var(--spacing-2)', fontSize: '1rem' }}>
              Are you sure you want to delete user <strong>{userToDelete}</strong>?
            </p>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
              This action cannot be undone.
            </p>
          </div>

          <ModalButtonGroup>
            <ModalButton
              onClick={() => {
                setShowDeleteConfirmModal(false);
                setUserToDelete(null);
              }}
              disabled={actionLoading !== null}
              style={{ 
                background: 'var(--color-surface-alt)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)'
              }}
            >
              Cancel
            </ModalButton>
            <ModalButton
              onClick={handleConfirmDelete}
              disabled={actionLoading !== null}
              $variant="primary"
              style={{ 
                background: '#ef4444',
                color: 'white',
                border: '1px solid #ef4444'
              }}
            >
              {actionLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 'var(--spacing-2)' }} />
                  Deleting...
                </>
              ) : (
                'Delete User'
              )}
            </ModalButton>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    </Section>
  );
};

